import type { ClientMessage, ServerMessage } from '../../shared/protocol';
import { PROTOCOL_VERSION } from '../../shared/constants';
import { ConnectIntent } from './connectIntent';

export type ConnStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';
type Listener = (msg: ServerMessage) => void;
type StatusListener = (s: ConnStatus) => void;

const TOKEN_KEY = 'bolt:sessionToken';

/**
 * Thin same-origin WebSocket client. The server is authoritative; this just
 * sends intents and receives role-filtered state. Survives drops: on reconnect
 * it resumes the player via the saved session token (§4.8.6 / Phase 4).
 */
export class SocketClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private backoff = 500;
  private explicitClose = false;
  // Idempotent actions in flight (idem → message). Resent on reconnect so a
  // play is never lost; the server dedupes by idem so it's never doubled (§4.8.6).
  private pending = new Map<string, ClientMessage>();
  /** v22 BOLT-108: a deliberate Create/Join from Home, armed until it lands. */
  private intent = new ConnectIntent();
  status: ConnStatus = 'closed';

  get token(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }
  setToken(t: string | null) {
    try {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* private mode */
    }
  }

  private url(): string {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}/ws`;
  }

  connect() {
    this.explicitClose = false;
    this.setStatus(this.ws ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(this.url());
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = 500;
      this.setStatus('open');
      // v22 BOLT-108: an explicit Create/Join beats the auto-rejoin; the
      // auto-rejoin is only for a genuine reconnect (no Home action).
      const action = this.intent.onOpen(!!this.token);
      if (action.t === 'intent') this.send(action.msg);
      else if (action.t === 'rejoin')
        this.send({ t: 'rejoin', sessionToken: this.token!, protocolVersion: PROTOCOL_VERSION });
      // Resend anything still unacked (rejoin reattaches first, FIFO).
      for (const msg of this.pending.values()) this.send(msg);
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.t === 'session') {
        this.setToken(msg.token);
        this.intent.onSession(); // v22 BOLT-108: the intent landed
      }
      if (msg.t === 'error') {
        const retry = this.intent.onError(msg.code);
        if (retry) this.send(retry);
      }
      for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      this.ws = null;
      if (this.explicitClose) {
        this.setStatus('closed');
        return;
      }
      this.setStatus('reconnecting');
      setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 1.7, 5000);
    };
    ws.onerror = () => ws.close();
  }

  send(msg: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /** Send an idempotent action and keep it queued until acked, so a drop mid
   *  flight doesn't lose it. The server dedupes by `idem`. */
  sendReliable(msg: ClientMessage & { idem: string }) {
    this.pending.set(msg.idem, msg);
    this.send(msg);
  }
  ack(idem: string) {
    this.pending.delete(idem);
  }

  close() {
    this.explicitClose = true;
    this.ws?.close();
  }

  /** Test hook: force-close the live socket to simulate a network drop. The
   *  onclose handler then reconnects (resending pending) just like a real drop. */
  dropForTest() {
    this.ws?.close();
  }

  /** Forget the current session (used when leaving / starting fresh). */
  forget() {
    this.setToken(null);
    this.pending.clear();
    this.intent.clear();
  }

  /** v22 BOLT-108: a deliberate new-game action from Home. Drops any stale
   *  resume token FIRST so the auto-rejoin can't hijack it, then QUEUES the
   *  message so it also survives a socket that is still mid-connect (a plain
   *  `send` is dropped when the socket isn't open — the cold-start QR case). */
  startFresh(msg: ClientMessage) {
    this.forget();
    this.intent.arm(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.send(msg);
  }

  on(l: Listener) {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  onStatus(l: StatusListener) {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }
  private setStatus(s: ConnStatus) {
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }
}

export const socket = new SocketClient();
// Expose for E2E network-drop simulation (harmless in production).
if (typeof window !== 'undefined') (window as unknown as { __boltSocket: SocketClient }).__boltSocket = socket;
